import React, { useCallback, useMemo, useState } from "react";
import { TextExtractor, findFirstComponentOfType } from "../../internal/TextExtractor";
import { evaluateNumberExpression } from "../../../shared/utils/expression.utils";

interface SubstringProps {
    string?: string,
    startindex?: number,
    endindex?: number,
    children?: React.ReactNode,
}

/***
 * A source component that extracts a substring from a string.
 * The string can be passed as a prop or as a child.
 * The substring is extracted between the startIndex and endIndex.
 * 
 * Params:
 *      string: The string to extract the substring from. If not provided, the string is expected to be in the arg-string child.
 *      startIndex: The start index of the substring. If not provided, the start index is expected to be in the arg-startindex child.
 *      endIndex: The end index of the substring. If not provided, the end index is expected to be in the arg-endindex child.
 */
export const Substring = React.memo(function Substring({ string, startindex, endindex, children }: SubstringProps): React.ReactElement {

    const stringChild = useMemo(() => findFirstComponentOfType(children, "arg-string"), [children]);
    const [stringValue, setStringValue] = useState(string);

    // Get the start index from the start index child
    const startIndexChild = useMemo(() => findFirstComponentOfType(children, "arg-startindex"), [children]);
    const [startIndexValue, setStartIndexValue] = useState(startindex);
    const setStartIndexStringValue = useCallback((value: string) => {
        if (value) {
            const numberValue = evaluateNumberExpression(value);
            if (!isNaN(numberValue)) {
                setStartIndexValue(numberValue);
            }
        }
    }, [setStartIndexValue]);

    // Get the end index from the end index child
    const endIndexChild = useMemo(() => findFirstComponentOfType(children, "arg-endindex"), [children]);
    const [endIndexValue, setEndIndexValue] = useState(endindex);
    const setEndIndexStringValue = useCallback((value: string) => {
        if (value) {
            const numberValue = evaluateNumberExpression(value);
            if (!isNaN(numberValue)) {
                setEndIndexValue(numberValue);
            }
        }
    }, [setEndIndexValue]);

    const resultSubstring = useMemo(() => {
        return stringValue?.substring(startIndexValue ?? 0, endIndexValue ?? stringValue.length) ?? "";
    }, [stringValue, startIndexValue, endIndexValue]);

    return <>
        {resultSubstring}
        {stringChild && <TextExtractor onExtractedText={setStringValue}>
            {stringChild.props.children}
        </TextExtractor>}
        {startIndexChild && <TextExtractor onExtractedText={setStartIndexStringValue}>
            {startIndexChild.props.children}
        </TextExtractor>}
        {endIndexChild && <TextExtractor onExtractedText={setEndIndexStringValue}>
            {endIndexChild.props.children}
        </TextExtractor>}
    </>;
});